import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { BikeService } from 'src/app/services/bike.service';
import { CyclistService } from 'src/app/services/cyclist.service';
import { HelpersService } from 'src/app/services/helpers.service';

@Component({
  selector: 'app-workshop-jobcard-list-customer-items',
  templateUrl: './workshop-jobcard-list-customer-items.component.html',
  styleUrls: ['./workshop-jobcard-list-customer-items.component.scss']
})
export class WorkshopJobcardListCustomerItemsComponent {

  @Input() jobcard: any;
  @Input() jobCards: any[] = []; // New input for job cards
  @Output() jobcardChange = new EventEmitter<any>();

  // Customers on the HTUniqueID integration receive the item ID with an "SBID" prefix
  // (e.g. SBID4048198) and will search on that whole value, so drop the prefix before
  // comparing. The prefix is applied by an external system, not by anything in this repo
  private static readonly ITEM_ID_SEARCH_PREFIX = 'sbid';

  public results: any[] = [];
  public filteredResults: any[] = [];
  public searchTerm: string = '';
  public isloading: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private translate: TranslateService,
    private bikeService: BikeService,
    private cyclistService: CyclistService,
    private helperService: HelpersService
  ) { }

  ngOnInit() {
    this.getCustomerItems();
  }

  ngAfterViewInit() {
    // Remove focus from any focused button when the modal loads
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.tagName === 'BUTTON') {
        activeElement.blur();
      }
    }, 100);
  }

  getCustomerItems() {
    this.isloading = true;
    this.cyclistService.getActiveItemsForCustomerAndPartner(this.jobcard.customer.ID, this.helperService.getPartnerId()).subscribe(response => {
      this.isloading = false;
      this.results = response;
      this.applySearchFilter();
      if (this.results.length === 0) {
        this.createNewItem();
      }
    }, err => {
      this.isloading = false;
    });
  }

  onSearchChange() {
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredResults = [...this.results];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();

    this.filteredResults = this.results.filter(item => this.matchesSearchTerm(item, searchLower));
  }

  private matchesSearchTerm(item: any, searchTerm: string): boolean {
    // An ID search is a separate intent from a text search, so test it on its own rather
    // than adding item.ID to the haystack below - the haystack matches on substrings, so an
    // ID in it would let a model year like "2023" match an ID that merely contains 2023
    if (this.matchesItemId(item, searchTerm)) {
      return true;
    }

    // Search across all the fields shown on the row, so what the user sees is what they can search
    const searchableFields = [
      item.Manufacturer,
      item.Model,
      item.SerialNo,
      item.Colour,
      item.BikeSize,
      item.ModelYear > 0 ? item.ModelYear.toString() : null
    ];

    // Match against the fields joined together rather than each field on its own, so a
    // multi-word search like "trek emonda" spanning Manufacturer and Model still matches.
    // Every word must be found somewhere in the row.
    const haystack = searchableFields.filter(field => field).join(' ').toLowerCase();

    return searchTerm.split(/\s+/).every(word => haystack.includes(word));
  }

  // Exact match only - a partial ID is not a meaningful search, and matching loosely would
  // let a model year or bike size collide with an unrelated item's ID. The search term is
  // already lowercased by applySearchFilter, which is what makes the prefix check
  // case-insensitive
  private matchesItemId(item: any, searchTerm: string): boolean {
    const prefix = WorkshopJobcardListCustomerItemsComponent.ITEM_ID_SEARCH_PREFIX;
    let idTerm = searchTerm;

    if (idTerm.startsWith(prefix)) {
      idTerm = idTerm.substring(prefix.length).trim();
    }

    return idTerm !== '' && idTerm === (item.ID ? item.ID.toString() : '');
  }

  selectItem(event: any, item: any) {
    event.stopPropagation();
    this.jobcard.selectedItem = {
      id: item.ID,
      manufacturer: item.Manufacturer,
      model: item.Model,
      serialNo: item.SerialNo,
      typeID: item.TypeID,
      colour: item.Colour,
      modelYear: item.ModelYear,
      bikeSize: item.BikeSize,
      lastService: item.LastService,
    };
    this.jobcardChange.emit({ ...this.jobcard });
    this.activeModal.close({ selectedItem: this.jobcard.selectedItem });
  }

  editItem(event: any, item: any) {
    event.stopPropagation();
    var selectedItem = {
      id: item.ID,
      manufacturer: item.Manufacturer,
      model: item.Model,
      serialNo: item.SerialNo,
      typeID: item.TypeID,
      colour: item.Colour,
      modelYear: item.ModelYear,
      bikeSize: item.BikeSize,
      lastService: item.LastService,
    }
    this.activeModal.close({ action: 'edit', selectedItem: selectedItem });
  }

  createNewItem() {
    this.activeModal.close({ action: 'create' });
  }

  @HostListener('document:keydown.enter', ['$event'])
  handleEnter(event: KeyboardEvent) {
    event.stopPropagation();
    // Don't hijack Enter into "create a new item" while the user is busy searching,
    // otherwise hitting Enter after typing a search closes the modal and loses the search
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      return;
    }
    setTimeout(() => {
      this.createNewItem();
    }, 100); // Delay to ensure the modal is ready    
  }

  checkIfItemUsed(item: any): boolean {
    // Check if the item is used in any of the job cards
    return this.jobCards.some(jobCard => {
      return jobCard.selectedItem && jobCard.selectedItem.id === item.ID;
    });
  }
}
